import { useState, useEffect } from 'react';

/**
 * Custom hook to detect if the current theme is dark.
 * Checks both explicit data-theme attribute and system preference.
 * 
 * @returns {boolean} true if dark theme is active, false otherwise
 */
export function useTheme() {
  const checkIsDarkTheme = () => {
    const dataTheme = document.documentElement.getAttribute('data-theme');
    if (dataTheme === 'dark') return true;
    if (dataTheme) return false; // Explicit light theme
    // No explicit theme - check system preference
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  };

  const [isDarkTheme, setIsDarkTheme] = useState(checkIsDarkTheme);

  useEffect(() => {
    const handleThemeChange = () => setIsDarkTheme(checkIsDarkTheme());

    // Watch for data-theme attribute changes
    const observer = new MutationObserver(handleThemeChange);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });

    // Watch for system preference changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', handleThemeChange);

    return () => {
      observer.disconnect();
      mediaQuery.removeEventListener('change', handleThemeChange);
    };
  }, []);

  return isDarkTheme;
}

export default useTheme;
